import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, Search, ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Spinner } from "../components/common";
import { pickAirportBg } from "../lib/assets";

const CONTINENTS = { AF: "Africa", AN: "Antarctica", AS: "Asia", EU: "Europe", NA: "North America", OC: "Oceania", SA: "South America" };

/* ISO-2 country code → 🇺🇸-style flag emoji via regional-indicator codepoints. */
function flagEmoji(iso2) {
  if (!iso2 || iso2.length !== 2) return "🌐";
  const A = 0x1f1e6, ORD = "A".charCodeAt(0);
  try {
    return String.fromCodePoint(A + iso2.charCodeAt(0) - ORD, A + iso2.charCodeAt(1) - ORD);
  } catch { return "🌐"; }
}

export default function Countries() {
  const [q, setQ] = useState("");
  const [continent, setContinent] = useState("");
  const [region, setRegion] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/countries", { params: { q, continent, region } }).then((r) => setList(r.data)).finally(() => setLoading(false));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, continent, region]);

  const REGIONS = [
    { key: "", label: "All regions" },
    { key: "usa", label: "USA" },
    { key: "europe", label: "Europe" },
    { key: "asia", label: "Asia" },
    { key: "india", label: "India" },
  ];

  return (
    <div data-testid="countries-page">
      <PageHeader eyebrow="Directory" title="Countries" subtitle="Browse aviation infrastructure across 249 countries and territories." />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">

        {/* Editorial section header */}
        <div className="mb-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-amber-700 inline-flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5" /> Every corner of the world
          </div>
          <h2 className="font-serif font-800 text-3xl sm:text-4xl text-slate-900 leading-tight tracking-tight mt-1">
            Explore by <span className="italic text-amber-700">Country</span>
          </h2>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-4">
          {REGIONS.map((r) => (
            <button key={r.key} onClick={() => { setRegion(r.key); if (r.key) setContinent(""); }} data-testid={`countries-region-${r.key || "all"}`}
              className={`px-4 py-1.5 text-xs font-mono uppercase tracking-[0.18em] rounded-full border-2 transition-colors
                ${region === r.key
                  ? "bg-amber-600 text-white border-amber-600 shadow-sm"
                  : "bg-white text-slate-700 border-amber-200 hover:border-amber-500"}`}>
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input data-testid="countries-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search country…"
              className="w-full border-2 border-amber-200 focus:border-amber-500 bg-white pl-9 pr-3 py-3 rounded-xl text-sm outline-none" />
          </div>
          <select data-testid="countries-continent-filter" value={continent} onChange={(e) => { setContinent(e.target.value); setRegion(""); }}
            className="border-2 border-amber-200 focus:border-amber-500 bg-white px-4 py-3 rounded-xl text-sm outline-none">
            <option value="">All continents</option>
            {Object.entries(CONTINENTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>

        {loading ? <Spinner /> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {list.map((c) => {
              const img = pickAirportBg(c.name);
              return (
                <Link key={c.id} to={`/countries/${c.id}`} data-testid={`country-card-${c.id}`}
                  className="group relative rounded-2xl overflow-hidden shadow-md hover:shadow-xl hover:-translate-y-1 transition-all border border-amber-100/50 bg-white flex flex-col">
                  <div className="relative aspect-[5/4] overflow-hidden">
                    <img src={img} alt={c.name} loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[10px] font-mono uppercase tracking-[0.18em] text-slate-800 shadow-sm">
                      <span className="text-sm leading-none">{flagEmoji(c.id)}</span> {c.id}
                    </div>
                    <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-amber-500/95 backdrop-blur text-[10px] font-mono uppercase tracking-[0.18em] text-white shadow-sm">
                      {CONTINENTS[c.continent] || c.continent || "—"}
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-4">
                      <div className="font-serif font-800 text-2xl sm:text-3xl text-white leading-[1.05] tracking-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
                        {c.name}
                      </div>
                    </div>
                  </div>
                  <div className="p-3 flex items-center justify-between"
                    style={{ background: "linear-gradient(180deg, #faf7f0 0%, #ffffff 100%)" }}>
                    <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">
                      {CONTINENTS[c.continent] || "World"}
                    </div>
                    <span className="inline-flex items-center gap-0.5 text-amber-700 text-xs font-800 group-hover:gap-1.5 transition-all">
                      Explore <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              );
            })}
            {list.length === 0 && (
              <div className="col-span-full text-center text-sm text-slate-500 py-8">
                No countries match "{q}". Try clearing the region filter.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
